import { useMutation } from "@tanstack/react-query";
import { useI18n } from "i18n";
import { Errors, trackError } from "modules/monitoring";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { useSelector } from "react-redux";
import { selectUserId } from "modules/auth/redux/selectors";
import { queryClient } from "services/apiService/queryClient";
import { useAppDispatch } from "store/hooks";
import { setShowForm } from "modules/registration/redux/slice";
import { submitAnswers as submitAnswersRequest } from "./request";
import {
  CUSTOM_ANSWERS_QUERY_KEY,
  CustomRegistrationQuestionsErrors,
} from "../../constants";

interface Options {
  onSuccess: () => void;
}

export const useAnswerOperations = (
  eventId: string | undefined,
  options: Options,
) => {
  const { onSuccess } = options;
  const dispatch = useAppDispatch();
  const userId = useSelector(selectUserId);
  const { addErrorNotification } = useNotificationActions();
  const { t } = useI18n(["eventForm"]);

  const {
    mutateAsync: submitAnswers,
    mutate: submitAnswersSync,
    isPending,
    status,
  } = useMutation({
    mutationFn: async (answers: Record<string, string>) => {
      if (!eventId) {
        throw new Error(CustomRegistrationQuestionsErrors.NO_EVENT_ID);
      }
      if (!userId) {
        throw new Error(CustomRegistrationQuestionsErrors.NO_USER_ID);
      }

      return submitAnswersRequest(eventId, answers);
    },

    onSuccess: (answers) => {
      queryClient.setQueryData(
        [CUSTOM_ANSWERS_QUERY_KEY, eventId, userId],
        answers,
      );
      dispatch(setShowForm(false));
      onSuccess();
    },

    onError: (error: Error, _) => {
      addErrorNotification({
        message: t("custom.registration.answers.save.error", {
          error: error.message,
        }),
        position: "tc",
      });
      trackError(error, {
        label: Errors.CUSTOM_REGISTRATION_ANSWERS_SUBMIT,
      });
    },
  });

  return {
    submitAnswers,
    submitAnswersSync,
    isPending,
    status,
  };
};
